import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError, setToken, AUTH_EXPIRED_EVENT } from '../api';
import { authMode, getFirebaseAuth } from './firebase';
import { logAnalyticsEvent } from './analytics';
import { queryClient } from '../data/queryClient';
import type { AuthState, AuthUser, SignInInput, SignUpInput } from './types';
import type { LegalDocument } from '../data/dto';

interface AuthContextValue extends AuthState {
  signUpWithEmail: (input: SignUpInput) => Promise<void>;
  signInWithEmail: (input: SignInInput) => Promise<void>;
  /**
   * `acceptedTerms` is only meaningful when this Google sign-in is actually a
   * signup (AuthStep's "Create account" mode with the checkbox checked) — it
   * tells the token-exchange handler below to persist that acceptance.
   */
  signInWithGoogle: (acceptedTerms?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  changeEmail: (password: string, newEmail: string) => Promise<void>;
  changePassword: (password: string, newPassword: string) => Promise<void>;
  /** Clear an error surfaced from the last auth call. */
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const MOCK_USER_KEY  = 'babysteps.mock.user';
const MOCK_JWT_KEY   = 'babysteps.jwt';

interface BackendVerifyResponse {
  token: string;
}

/**
 * Wraps the app and exposes auth state + actions via the `useAuth` hook.
 *
 * Two modes:
 *   - 'firebase' — uses Firebase Auth SDK. After Firebase signin, we POST the
 *     ID token to /api/v1/auth/verify to mint a backend JWT.
 *   - 'mock' — bypasses Firebase entirely. Useful for UI development before
 *     credentials are wired up. Persists a fake user + JWT in localStorage.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    user: null,
    error: null,
  });
  const unsubRef = useRef<(() => void) | null>(null);
  // Set by signUpWithEmail/signInWithGoogle right before kicking off Firebase,
  // consumed once the resulting onAuthStateChanged round-trip mints a backend
  // JWT below — records the signup-time terms acceptance so a brand-new user
  // never sees LegalAcceptanceScreen right after already checking the box.
  // Purely a UX shortcut: if this never fires (e.g. Firebase init races an
  // unrelated auth-state change), AuthMiddleware's gate catches it on the
  // next request instead.
  const pendingLegalAcceptanceRef = useRef(false);

  // ── Bootstrap: restore session if we have one ────────────────────────
  useEffect(() => {
    let cancelled = false;

    const finish = (user: AuthUser | null) => {
      if (cancelled) return;
      setState({
        status: user ? 'authenticated' : 'unauthenticated',
        user,
        error: null,
      });
    };

    if (authMode === 'mock') {
      try {
        const raw = localStorage.getItem(MOCK_USER_KEY);
        if (raw && localStorage.getItem(MOCK_JWT_KEY)) {
          const user: AuthUser = JSON.parse(raw);
          if (!user.providerId) {
            user.providerId = user.uid.startsWith('mock-google-') ? 'google.com' : 'password';
          }
          finish(user);
          return () => {
            cancelled = true;
          };
        }
      } catch {
        /* fall through to unauthenticated */
      }
      finish(null);
    } else {
      // Firebase mode: subscribe to auth state changes.

      // Clear any stale mock-mode JWT before Firebase init. A real backend
      // JWT has three dot-separated segments; "mock-token-..." doesn't, and
      // sending it would 401 on every authenticated request.
      const staleToken = (() => {
        try { return localStorage.getItem(MOCK_JWT_KEY); } catch { return null; }
      })();
      if (staleToken && !looksLikeJwt(staleToken)) {
        // eslint-disable-next-line no-console
        console.info('[auth] clearing stale mock token from localStorage');
        setToken(null);
        try { localStorage.removeItem(MOCK_USER_KEY); } catch { /* no-op */ }
      }

      (async () => {
        try {
          const auth = await getFirebaseAuth();
          const { onAuthStateChanged } = await import('firebase/auth');
          unsubRef.current = onAuthStateChanged(auth, async (fbUser) => {
            if (cancelled) return;
            if (!fbUser) {
              // eslint-disable-next-line no-console
              console.info('[auth] firebase signed out');
              setToken(null);
              finish(null);
              return;
            }
            try {
              const idToken = await fbUser.getIdToken();
              // eslint-disable-next-line no-console
              console.info('[auth] firebase signed in as', fbUser.email, '— exchanging for backend JWT');
              const res = await api<BackendVerifyResponse>('/auth/verify', {
                method: 'POST',
                body: { id_token: idToken },
              });
              setToken(res.token);
              // eslint-disable-next-line no-console
              console.info('[auth] backend JWT issued — authenticated');
              if (pendingLegalAcceptanceRef.current) {
                pendingLegalAcceptanceRef.current = false;
                recordSignupLegalAcceptance();
              }
              finish({
                uid: fbUser.uid,
                email: fbUser.email,
                displayName: fbUser.displayName,
                photoURL: fbUser.photoURL,
                providerId: fbUser.providerData[0]?.providerId || 'password',
              });
            } catch (e) {
              const msg = e instanceof ApiError
                ? `Backend rejected token (${e.status})`
                : e instanceof Error
                ? e.message
                : 'Backend exchange failed';
              // eslint-disable-next-line no-console
              console.error('[auth] /auth/verify failed:', e);
              setState({ status: 'unauthenticated', user: null, error: msg });
            }
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Firebase init failed';
          // eslint-disable-next-line no-console
          console.error('[auth] firebase init failed:', e);
          if (!cancelled) {
            setState({ status: 'unauthenticated', user: null, error: msg });
          }
        }
      })();
    }

    return () => {
      cancelled = true;
      unsubRef.current?.();
      unsubRef.current = null;
    };
  }, []);

  // ── Actions ──────────────────────────────────────────────────────────

  const setError = useCallback((message: string) => {
    setState((s) => ({ ...s, error: message }));
  }, []);

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  const signUpWithEmail = useCallback(
    async (input: SignUpInput) => {
      if (!input.acceptedTerms) {
        setError('You must accept the Terms and Conditions to continue.');
        return;
      }
      if (authMode === 'mock') {
        const user: AuthUser = {
          uid: 'mock-' + Math.random().toString(36).slice(2, 10),
          email: input.email,
          displayName: input.displayName ?? input.email.split('@')[0],
          photoURL: null,
          providerId: 'password',
        };
        localStorage.setItem(MOCK_USER_KEY, JSON.stringify(user));
        setToken('mock-token-' + user.uid);
        setState({ status: 'authenticated', user, error: null });
        return;
      }
      try {
        pendingLegalAcceptanceRef.current = true;
        const auth = await getFirebaseAuth();
        const { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } = await import('firebase/auth');
        const cred = await createUserWithEmailAndPassword(auth, input.email, input.password);
        if (input.displayName && !cred.user.displayName) {
          await updateProfile(cred.user, { displayName: input.displayName });
        }
        await sendEmailVerification(cred.user);
        logAnalyticsEvent('sign_up', { method: 'email' });
        // onAuthStateChanged will fire and set authenticated state.
      } catch (e) {
        pendingLegalAcceptanceRef.current = false;
        setError(humaniseAuthError(e));
      }
    },
    [setError],
  );

  const signInWithEmail = useCallback(
    async (input: SignInInput) => {
      if (authMode === 'mock') {
        // Reuse a previously stored mock user if email matches; otherwise mint a new one.
        try {
          const raw = localStorage.getItem(MOCK_USER_KEY);
          const existing = raw ? (JSON.parse(raw) as AuthUser) : null;
          const user: AuthUser =
            existing && existing.email === input.email
              ? existing
              : {
                  uid: 'mock-' + Math.random().toString(36).slice(2, 10),
                  email: input.email,
                  displayName: input.email.split('@')[0],
                  photoURL: null,
                  providerId: 'password',
                };
          localStorage.setItem(MOCK_USER_KEY, JSON.stringify(user));
          setToken('mock-token-' + user.uid);
          setState({ status: 'authenticated', user, error: null });
        } catch {
          setError('Could not sign in (mock mode storage error).');
        }
        return;
      }
      try {
        const auth = await getFirebaseAuth();
        const { signInWithEmailAndPassword } = await import('firebase/auth');
        await signInWithEmailAndPassword(auth, input.email, input.password);
        logAnalyticsEvent('login', { method: 'email' });
      } catch (e) {
        setError(humaniseAuthError(e));
      }
    },
    [setError],
  );

  const signInWithGoogle = useCallback(async (acceptedTerms?: boolean) => {
    if (authMode === 'mock') {
      const user: AuthUser = {
        uid: 'mock-google-' + Math.random().toString(36).slice(2, 10),
        email: 'demo@babysteps.app',
        displayName: 'Demo Parent',
        photoURL: null,
        providerId: 'google.com',
      };
      localStorage.setItem(MOCK_USER_KEY, JSON.stringify(user));
      setToken('mock-token-' + user.uid);
      setState({ status: 'authenticated', user, error: null });
      return;
    }
    try {
      if (acceptedTerms) pendingLegalAcceptanceRef.current = true;
      const auth = await getFirebaseAuth();
      const { GoogleAuthProvider, signInWithPopup, getAdditionalUserInfo } = await import('firebase/auth');
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(auth, provider);
      const isNewUser = !!getAdditionalUserInfo(cred)?.isNewUser;
      logAnalyticsEvent(isNewUser ? 'sign_up' : 'login', { method: 'google' });
    } catch (e) {
      pendingLegalAcceptanceRef.current = false;
      setError(humaniseAuthError(e));
    }
  }, [setError]);

  const doSignOut = useCallback(async () => {
    queryClient.clear();
    if (authMode === 'mock') {
      localStorage.removeItem(MOCK_USER_KEY);
      setToken(null);
      setState({ status: 'unauthenticated', user: null, error: null });
      return;
    }
    try {
      logAnalyticsEvent('logout');
      const auth = await getFirebaseAuth();
      const { signOut } = await import('firebase/auth');
      await signOut(auth);
      setToken(null);
      setState({ status: 'unauthenticated', user: null, error: null });
    } catch (e) {
      setError(humaniseAuthError(e));
    }
  }, [setError]);

  useEffect(() => {
    const handleAuthExpired = () => {
      void doSignOut();
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    };
  }, [doSignOut]);

  const resendVerificationEmail = useCallback(async () => {
    if (authMode === 'mock') {
      return;
    }
    try {
      const auth = await getFirebaseAuth();
      if (auth.currentUser) {
        const { sendEmailVerification } = await import('firebase/auth');
        await sendEmailVerification(auth.currentUser);
      }
    } catch (e) {
      setError(humaniseAuthError(e));
      throw e;
    }
  }, [setError]);

  const sendPasswordReset = useCallback(async (email: string) => {
    if (authMode === 'mock') {
      return;
    }
    try {
      const auth = await getFirebaseAuth();
      const { sendPasswordResetEmail } = await import('firebase/auth');
      await sendPasswordResetEmail(auth, email);
    } catch (e) {
      setError(humaniseAuthError(e));
      throw e;
    }
  }, [setError]);

  const changeEmail = useCallback(async (password: string, newEmail: string) => {
    if (authMode === 'mock') {
      setState((s) => {
        if (!s.user) return s;
        const updated = { ...s.user, email: newEmail };
        localStorage.setItem(MOCK_USER_KEY, JSON.stringify(updated));
        return { ...s, user: updated };
      });
      await api('/dev/change-email', {
        method: 'POST',
        body: { email: newEmail },
      });
      return;
    }
    try {
      const auth = await getFirebaseAuth();
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw new Error('No user is currently signed in.');
      }
      const { EmailAuthProvider, reauthenticateWithCredential, verifyBeforeUpdateEmail } = await import('firebase/auth');
      
      // 1. Re-authenticate
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);
      
      // 2. Trigger secure email update verification flow
      await verifyBeforeUpdateEmail(currentUser, newEmail);
    } catch (e) {
      setError(humaniseAuthError(e));
      throw e;
    }
  }, [setError]);

  const changePassword = useCallback(async (password: string, newPassword: string) => {
    if (authMode === 'mock') {
      return;
    }
    try {
      const auth = await getFirebaseAuth();
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw new Error('No user is currently signed in.');
      }
      const { EmailAuthProvider, reauthenticateWithCredential, updatePassword } = await import('firebase/auth');
      
      // 1. Re-authenticate
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);
      
      // 2. Update password
      await updatePassword(currentUser, newPassword);
    } catch (e) {
      setError(humaniseAuthError(e));
      throw e;
    }
  }, [setError]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signUpWithEmail,
      signInWithEmail,
      signInWithGoogle,
      signOut: doSignOut,
      resendVerificationEmail,
      sendPasswordReset,
      changeEmail,
      changePassword,
      clearError,
    }),
    [state, signUpWithEmail, signInWithEmail, signInWithGoogle, doSignOut, resendVerificationEmail, sendPasswordReset, changeEmail, changePassword, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * Fire-and-forget: persist acceptance of whatever the current Terms/Privacy
 * versions are, right after a signup mints its first JWT. Failures are
 * logged only — AuthMiddleware's legal_acceptance_required gate is the
 * correctness backstop if this never lands.
 */
function recordSignupLegalAcceptance(): void {
  api<{ documents: LegalDocument[] }>('/legal/documents')
    .then((res) =>
      api('/me/legal-acceptances', {
        method: 'POST',
        body: { acceptances: res.documents.map((d) => ({ type: d.type, version: d.version })) },
      })
    )
    .catch((e) => {
      // eslint-disable-next-line no-console
      console.error('[auth] failed to record signup legal acceptance:', e);
    });
}

/**
 * Loose JWT sniff: real backend tokens are three base64url segments joined by
 * dots. Returns false for "mock-token-..." strings and empty values.
 */
function looksLikeJwt(token: string): boolean {
  const parts = token.split('.');
  return parts.length === 3 && parts.every((p) => /^[A-Za-z0-9_-]+$/.test(p));
}

/** Map Firebase error codes to short human-readable messages. */
function humaniseAuthError(e: unknown): string {
  if (!e || typeof e !== 'object') return 'Something went wrong. Please try again.';
  const code = String((e as { code?: unknown }).code ?? '');
  switch (code) {
    case 'auth/email-already-in-use':
      return 'An account already exists for that email. Try signing in instead.';
    case 'auth/invalid-email':
      return 'That email address looks invalid.';
    case 'auth/weak-password':
      return 'Password is too weak — at least 6 characters.';
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Current password or credential is incorrect.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return e instanceof Error && e.message ? e.message : 'Something went wrong. Please try again.';
  }
}
